import { useState, useContext } from "react";
import { Box, Divider, Typography, Popover } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { KeyboardArrowUpRounded } from "@mui/icons-material";
import { logger } from "../../services/logger";
import { ReactComponent as IconSignOut } from "../../assets/IconSignOut.svg";
import { UniversalModalContext } from "../../context/UniversalModalContext";
import { CourseStructureCtx, UniversalModalCtx } from "../../interfaces";
import { generateGtmEvent, HandleUserLogout } from "../../Helpers";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";
import { _AuthType } from "../../enums";

interface userDropdownProps {
  userInfo: {
    initials: string;
    firstName: string;
    formattedName: string;
    email: string;
    accessibility: boolean;
  };
}

const useStyles = makeStyles((theme) => ({
  menuWrapper: {
    fontFamily: "Roboto, sans-serif",
    padding: "0 0.938rem",
  },
  menuPaper: {
    boxShadow: "0px 3px 20px #00000014",
  },
  name: {
    fontSize: 18,
    fontWeight: 500,
    letterSpacing: 0,
  },
  menuItem: {
    marginTop: "1rem",
    width: "100%",
  },
  popoverPaper: {
    boxShadow: "0px 3px 20px #00000014",
    padding: "1rem",
    minWidth: "15.625em",
    borderRadius: "8px",
    border: `2px solid ${theme.palette.divider}`,
  },
  buttonItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    textAlign: "left",
    padding: "8px 16px",
    background: "none",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    outline: "none",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
    "&:active": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
    "&:focus": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  menuItemWrapper: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    minWidth: "15.625em",
    width: "100%",
  },
  menuName: {
    fontSize: 20,
    fontWeight: 700,
    padding: "0.25rem 1rem",
    margin: 0,
  },
  emailName: {
    fontSize: "1rem",
    padding: "0.25rem 1rem",
    margin: 0,
  },
  dropdownButton: {
    alignItems: "center",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    height: 48,
    justifyContent: "center",
    padding: "8px 16px",
    width: "max-content",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#F3F3F3",
    },
    "&:focus": {
      backgroundColor: "#F3F3F3",
    },
  },
  buttonHover: {
    cursor: "pointer",
  },
  breakerLine: {
    backgroundColor: "#5d5d5d",
    height: "1px",
    width: "90%",
  },
  divider: {
    margin: "1em 1em",
  },
  root: {
    "&.MuiButtonBase-root": {
      paddingTop: ".25em",
      paddingBttom: ".25em",
    },
  },
  arrowDown: {
    height: "fit-content",
    transform: "rotate(0deg)",
    transition: "transform .2s linear",
  },
  arrowUp: {
    height: "fit-content",
    transform: "rotate(180deg)",
    transition: "transform .2s linear",
  },
}));

export const UserDropdown = ({ userInfo }: userDropdownProps) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();

  const editText =
    courseMetadata?.hide_learning_preferences &&
    courseMetadata?.hide_accessibility_toggle &&
    MacsAuthenticator.authHook.authType === _AuthType.LTI &&
    courseMetadata?.disable_change_password
      ? "View Account Details"
      : "Edit Account";

  const editTextAriaLabelId = `${editText.toLowerCase().replace(" ", "-")}-text`;

  const HandleDataLayer = (
    event: string,
    action: string,
    region: string,
    section: string,
    text: string,
    type?: string
  ) => {
    generateGtmEvent({
      event: event,
      action: action,
      name: "onclick",
      type: type ? type : "click",
      region: region,
      section: section,
      text: text,
    });
  };
  const handleClick = (e: any) => {
    setIsOpen(true);
    HandleDataLayer("collapse", "open", "navbar", "main navbar", "profile dropdown");
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setIsOpen(false);
    HandleDataLayer("collapse", "close", "navbar", "main navbar", "profile dropdown");
    setAnchorEl(null);
  };
  const handleSignOutButtonClick = async () => {
    handleClose();
    try {
      await HandleUserLogout({
        clearCognito: true,
        clearCookies: true,
        clearSession: true,
        ...(MacsAuthenticator.authHook.authType === _AuthType.LTI && {
          redirectToSessionEnd: true,
        }),
        redirectToLogin: true,
      });
    } catch (error) {
      logger.error(error);
    }
    handleClose();
  };
  const showLogoutButton = () => {
    if (MacsAuthenticator.authHook.authType === _AuthType.LTI) {
      return !courseMetadata?.disable_logout;
    }
    return true;
  };

  return (
    <>
      <button
        id="user-menu-toggle"
        type="button"
        className={classes.dropdownButton}
        onClick={(e) => handleClick(e)}
        aria-label={userInfo.initials + " " + userInfo.firstName}
        aria-expanded={isOpen}
        aria-controls="user-menu-container"
        style={
          isOpen
            ? {
                backgroundColor: "#F3F3F3",
              }
            : undefined
        }>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: "#E2F7FF",
            marginRight: "12px",
            position: "relative",
          }}>
          <Typography
            variant="h5"
            component="span"
            className={classes.name}
            data-sentry-mask>
            {userInfo.initials}
          </Typography>
        </Box>
        <Typography
          variant="h5"
          component="p"
          data-sentry-mask
          className={classes.name + " data-hj-suppress"}>
          {userInfo.firstName}
        </Typography>
        <KeyboardArrowUpRounded
          className={isOpen ? classes.arrowDown : classes.arrowUp}
        />
      </button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        role="menu"
        disableScrollLock
        PaperProps={{ className: classes.popoverPaper }}>
        <Typography
          component="p"
          variant="h4"
          data-sentry-mask
          className={`${classes.menuName} data-hj-suppress`}>
          {userInfo.formattedName}
        </Typography>
        <Typography
          component="p"
          variant="body2"
          data-sentry-mask
          className={`${classes.emailName} data-hj-suppress`}>
          {userInfo.email}
        </Typography>
        <Divider className={classes.divider} />
        <Typography
          id={editTextAriaLabelId}
          component="button"
          type="button"
          variant="body2"
          fontWeight={550}
          className={classes.buttonItem}
          aria-label={`${editText} (opens in dialog box)`}
          onClick={() => {
            universalModalCtx.setUserAccountModalOpen(true);
            handleClose();
          }}>
          {editText}
        </Typography>
        {showLogoutButton() && (
          <Typography
            component="button"
            type="button"
            variant="body2"
            fontWeight={550}
            color="#CC2F2F"
            className={classes.buttonItem}
            onClick={handleSignOutButtonClick}>
            <span>Log Out </span>
            <IconSignOut style={{ marginLeft: "auto", width: "16px", height: "16px" }} />
          </Typography>
        )}
      </Popover>
    </>
  );
};
